/* eslint-disable prefer-rest-params */

import type { AxiosError } from 'axios';
import Axios from 'axios';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { appEnv, authApiHost, isLocalDevelopment } from '../app-config';
// Stale-chunk detection and the one-shot reload guard live in the survey-tool submodule,
// which is compiled as first-party source here. Single-sourcing them there is what keeps
// the reload-guard sessionStorage key from being a contract duplicated across two repos.
import { isDynamicImportError, reloadOnce } from '../survey-tool/utils/dynamic-import-recovery';

const sent: string[] = [];

// Sliding-window rate limiter: prevent error/render loops from flooding the server.
// Silent drop — never log here (console.error is intercepted by this module).
const WINDOW_MS = 10_000;
const MAX_POSTS_PER_WINDOW = 30;
const recentSendTimestamps: number[] = [];

// A single searchable string built from the fields an error can arrive in:
// `name`/`message` (window.onerror & unhandledrejection paths) and `arguments`
// (intercepted console.error). Used by the substring-based suppression rules below.
function combinedText(data: ISubmitData): string {
  const message = typeof data.message === 'string' ? data.message : '';
  const args = Array.isArray(data.arguments) ? data.arguments.join(' ') : '';
  return `${data.name ?? ''} ${message} ${args}`;
}

// Errors thrown by browser extensions / injected third-party scripts, never by our
// own code: none of the identifiers or APIs below are used anywhere in the app
// (verified by grep before each addition — see the per-entry justification comments),
// so any error mentioning them is injected content.
const THIRD_PARTY_NOISE_SIGNATURES: readonly string[] = [
  'addListener', // extension content scripts: `chrome.runtime.onMessage.addListener` on an undefined API
  'chrome.storage', // extension storage access
  'runtime.sendMessage', // extension messaging (e.g. "Invalid call to runtime.sendMessage(). Tab not found.")
  'gtmObject', // Google Tag Manager container noise
  "COOKIEBOT_VERSION' has already been declared", // cookie-consent script double-injection
  "ONETRUST_VERSION' has already been declared", // cookie-consent script double-injection
  'MetaMask', // crypto-wallet extension
  'requestIdleCallback', // absent in some in-app/extension webviews
  'window.ethereum', // injected crypto-wallet provider (app uses no wallet)
  '__firefox__', // Firefox reader-mode / injected globals
  'No Listener: tabs:', // browser-extension messaging (webextension polyfill)
  'Cannot redefine property: Symbol(', // extension patching built-ins (Symbol.hasInstance)
  'A listener indicated an asynchronous response', // webextension messaging: channel closed before reply
  '__gCrWeb', // Chrome-iOS injected global
  'WKWebView API client did not respond', // iOS in-app webview bridge
  '[mediaBus] MediaSDK failed to load', // third-party media SDK loader; scoped to the load failure, not every [mediaBus] error
];

// Error *names* (not message substrings) that only third-party injected code produces.
// Matched exactly against `data.name`, deliberately NOT as a substring of combinedText:
// `UnavailableError` as a substring would also swallow a genuine `ServiceUnavailableError`
// from our own code or the backend.
const THIRD_PARTY_ERROR_NAMES: ReadonlySet<string> = new Set([
  'UnavailableError', // PayPal Honey Safari extension (also caught by isInjectedOrigin when a stack is present)
]);

// Expected/benign or transient messages that are not actionable app bugs: a
// user-facing validation message, request timeouts, and an unpinnable circular-
// structure serialization thrown by third-party/analytics code (tracked for a
// sourcemap root-cause in docs/TODO.md).
const BENIGN_NOISE_SIGNATURES: readonly string[] = [
  "We've already sent an email to your address recently", // email-resend throttle (user-facing validation)
  'timeout exceeded', // axios/network request timeout
  'Converting circular structure to JSON', // circular-ref serialization (third-party/analytics; unpinned)
  'ResizeObserver loop', // benign browser notification, no user impact
];

// Errors whose ORIGIN is injected extension / userscript code rather than our bundle.
// Checked against `stack`/`source` only: these markers never appear in a message, and
// widening combinedText() to include stack text would broaden every message rule above.
const INJECTED_ORIGIN_SIGNATURES: readonly string[] = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'webkit-masked-url://', // Safari userscript / content-blocker injected module
  '.safariextension/', // Safari app-extension resource bundle (e.g. PayPal Honey)
  'file:///Applications/', // macOS app extension serving its own JS
];

function isInjectedOrigin(data: ISubmitData): boolean {
  const stack = typeof data.stack === 'string' ? data.stack : '';
  const source = typeof data.source === 'string' ? data.source : '';
  const origin = `${stack} ${source}`;
  return INJECTED_ORIGIN_SIGNATURES.some((sig) => origin.includes(sig));
}

// Transient network failures (offline, connection drop, request timeout).
// Deliberately EXCLUDES Safari's bare "Load failed": Safari uses that same generic string
// for a failed module/chunk fetch, so suppressing it could hide a stale-chunk failure that
// carries no dynamic-import marker and therefore gets no reload recovery either — the worst
// of both outcomes. 'Load failed' stays visible until it can be distinguished by origin.
const NETWORK_TRANSIENT_SIGNATURES: readonly string[] = ['Failed to fetch', 'operation timed out'];

// Defence in depth: submitData() now handles dynamic-import errors before any suppression
// runs, so this guard should be unreachable from that path. It is kept because isUseless()
// is exported and called directly (including by tests), and because 'Failed to fetch' is a
// strict substring of Vite's 'Failed to fetch dynamically imported module'.
function isNetworkTransient(data: ISubmitData): boolean {
  const text = combinedText(data);
  if (isDynamicImportError(text)) {
    return false;
  }
  return NETWORK_TRANSIENT_SIGNATURES.some((sig) => text.includes(sig));
}

// Server-side 4xx/5xx responses are expected application/business-rule outcomes
// (validation errors, not-found, rate limiting, etc.), not client bugs — drop them
// from client-side error logging regardless of capture shape (structured axiosErr,
// or the message-only text surfaced via console.error/unhandledrejection). Per
// product decision this intentionally drops ALL 4xx/5xx, including non-session-
// expiry 401s (see the isSessionExpiry rule below for the 401-specific carve-out
// this supersedes for logging purposes).
function isServerHttpError(data: ISubmitData): boolean {
  const status = data.axiosErr?.status ?? data.axiosErr?.response?.status;
  if (typeof status === 'number' && status >= 400) {
    return true;
  }
  return /Request failed with status code [45]\d{2}\b/.test(combinedText(data));
}

export function isUseless(data: ISubmitData, dataString: string): boolean {
  if (!data) {
    return true;
  }
  if (data.colno === 0 && data.lineno === 0 && !data.source && data.message === 'Script error.') {
    return true;
  }
  if (isServerHttpError(data)) {
    return true;
  }
  if (isInjectedOrigin(data)) {
    return true;
  }
  if (isNetworkTransient(data)) {
    return true;
  }
  if (typeof data.name === 'string' && THIRD_PARTY_ERROR_NAMES.has(data.name)) {
    return true;
  }
  if (Array.isArray(data.arguments) && data.arguments[0] === 'AxiosError: Request aborted') {
    return true;
  }
  if (data.axiosErr && (data.axiosErr.code === 'ERR_NETWORK' || data.axiosErr.code === 'ECONNABORTED')) {
    return true;
  }
  if (isEqual(omit(data, ['href']), { arguments: ['AxiosError: Network Error'] })) {
    return true;
  }
  // Suppress AxiosError network noise from the unhandledrejection path (no axiosErr object there)
  if (
    data.name === 'AxiosError' &&
    typeof data.message === 'string' &&
    (data.message === 'Network Error' ||
      data.message === 'Request aborted' ||
      data.message.startsWith('Network Error') ||
      data.message.startsWith('Request aborted'))
  ) {
    return true;
  }
  // Expected session-expiry. Only suppress a 401 that ApiCall actually handled as
  // session-expiry (cleared the token + redirected to login), which it marks with
  // `isSessionExpiry` (src/store/api/api-call.ts). A 401 on a request that opted out of
  // that treatment — a `doNotRemoveAuthCookie` OAuth connect/disconnect, or a 401 raised
  // inside the /login & /join flows — is NOT flagged and still logs, so genuine auth
  // bugs there stay visible.
  if (data.isSessionExpiry === true) {
    return true;
  }
  // Benign "…→ skipped" business-rule rejections from the fire-and-forget skip action:
  // the backend rejects a terminal-state→skipped transition, surfaced as an unhandled
  // ClientError rejection. Expected, not app bugs. Scoped to the known terminal states
  // so an unexpected non-terminal rejection (e.g. started→skipped) still logs.
  if (
    data.name === 'ClientError' &&
    typeof data.message === 'string' &&
    /^Can't change participation status from (completed|disqualified|quota_full|screened) to skipped!$/.test(
      data.message
    )
  ) {
    return true;
  }
  // Parse-time SyntaxErrors never come from our own compiled bundle (it is valid JS
  // by build time; a corrupt/half-loaded chunk surfaces as a dynamic-import failure,
  // handled by isReload). A SyntaxError whose `source` is the current document URL is an inline
  // <script> injected verbatim from admin/campaign-configured tracking-pixel HTML
  // (src/utils/tracking-tags.ts, or thank_you.tracking_pixel) — a config bug in that
  // third-party snippet, not an app bug. We ship no first-party inline scripts, so a
  // document-sourced SyntaxError is always injected third-party content. Errors from
  // real script resources (our .js/.mjs bundle, external tracking .js) keep a distinct
  // `source`, and no-`source`/empty-`source` rejections (e.g. JSON.parse via
  // unhandledrejection) never equal the page URL — all preserved.
  if (
    data.name === 'SyntaxError' &&
    typeof data.source === 'string' &&
    data.source.split('?')[0].split('#')[0] === data.href.split('?')[0].split('#')[0]
  ) {
    return true;
  }
  // Browser-extension / injected-third-party-script noise (see signatures above).
  const text = combinedText(data);
  if (THIRD_PARTY_NOISE_SIGNATURES.some((sig) => text.includes(sig))) {
    return true;
  }
  // Expected/benign or transient noise (see signatures above).
  if (BENIGN_NOISE_SIGNATURES.some((sig) => text.includes(sig))) {
    return true;
  }
  return false;
}

// `isDynamicImportError` and `reloadOnce` are re-exported from the survey-tool submodule
// (see the import at the top of this file), which owns the single implementation of
// stale-chunk detection and the one-shot reload guard. They are re-exported here because
// this module is where the rest of the app has always imported them from; the recovery
// logic itself is no longer duplicated in this repo.
export { isDynamicImportError, reloadOnce };

export function isReload(data: ISubmitData, dataString: string): boolean {
  if (!data) {
    return false;
  }
  if (isDynamicImportError(combinedText(data))) {
    return reloadOnce();
  }
  return false;
}

function isExcluded(data: ISubmitData, dataString: string): boolean {
  if (!data || !dataString) {
    return true;
  }
  if (isEqual(omit(data, 'href'), { colno: 0, lineno: 0, source: '', message: 'Script error.' })) {
    return true;
  }
  if (typeof data.source === 'string' && data.source.startsWith('https://googleads.g.doubleclick.net/')) {
    return true;
  }
  if (typeof data.arguments?.[0] === 'string') {
    if (
      data.arguments[0].startsWith('Asset') &&
      data.arguments[0].includes('got suspended, please visit https://anytrack.io for more details.')
    ) {
      return true;
    }
    if (data.arguments[0].startsWith('Pinterest Tag Error:')) {
      return true;
    }
  }
  return sent.includes(dataString);
}

interface ISubmitData {
  href: string;
  [other: string]: any;
  arguments?: any[];
  axiosErr?: AxiosError;
  extra?: Record<string, string | number | boolean | undefined>;
}

type LogLevel = 'info' | 'warn' | 'error';

// Strip query string and hash from a URL so credential tokens carried in query
// params (e.g. ?token=…, ?pwd-token=…) never reach the remote log store.
function sanitizeUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

// Only the page-location keys carry credential tokens in query params. `source`
// is a script URL (no tokens) and may be a bare filename, so it is left as-is.
const URL_ATTRIBUTE_KEYS = new Set(['href', 'url']);

// Strip query strings/hashes from any URLs embedded in a stack trace. Stack frames
// reference the page/asset URL, which on a token-bearing page (e.g. ?token=…) would
// otherwise carry the credential into the remote log store.
function sanitizeStack(stack: string): string {
  return stack.replace(/(https?:\/\/[^\s?#)]+)[?#][^\s)]*/g, '$1');
}

function buildPayload(level: LogLevel, data: ISubmitData) {
  // Derive a single message string. NEVER JSON.stringify the whole `data`: it can
  // carry an AxiosError whose toJSON() embeds request headers (incl. the user's
  // pp-token), which would leak into the log payload.
  let message: string;
  if (typeof data.message === 'string' && data.message) {
    message = data.message;
  } else if (Array.isArray(data.arguments) && data.arguments.length > 0) {
    message = data.arguments.join(' ');
  } else if (data.axiosErr) {
    message = `${data.axiosErr.name ?? 'AxiosError'}: ${data.axiosErr.message}`;
  } else {
    message = 'Unspecified client error';
  }

  // Attributes: primitives only, omit undefined values; URLs are sanitized.
  const attributes: Record<string, string | number | boolean> = {};
  const primitiveKeys = ['href', 'name', 'source', 'stack', 'lineno', 'colno', 'url'] as const;
  for (const key of primitiveKeys) {
    const val = data[key];
    if (
      val !== undefined &&
      val !== null &&
      (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
    ) {
      if (key === 'stack' && typeof val === 'string') {
        attributes[key] = sanitizeStack(val);
      } else if (typeof val === 'string' && URL_ATTRIBUTE_KEYS.has(key)) {
        attributes[key] = sanitizeUrl(val);
      } else {
        attributes[key] = val;
      }
    }
  }
  // Merge an optional caller-supplied `extra` bag of sanitized primitives (see
  // logErrorWithContext). Allow-list keys above always win on collision.
  if (data.extra && typeof data.extra === 'object') {
    for (const [key, val] of Object.entries(data.extra)) {
      if (val === null || val === undefined || key in attributes) {
        continue;
      }
      if (typeof val === 'string') {
        attributes[key] = val.slice(0, 500);
      } else if (typeof val === 'number' || typeof val === 'boolean') {
        attributes[key] = val;
      }
    }
  }
  // Carry axios info if present
  if (data.axiosErr) {
    const { code, status, response } = data.axiosErr;
    if (code) {
      attributes.axiosCode = String(code);
    }
    const resolvedStatus = status ?? response?.status;
    if (resolvedStatus !== undefined) {
      attributes.axiosStatus = Number(resolvedStatus);
    }
  }

  return {
    service: 'pureprofile-app' as const,
    level,
    message,
    ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
  };
}

export function submitData(level: LogLevel, data: ISubmitData) {
  if (isLocalDevelopment) {
    return;
  }
  let dataString: string;
  try {
    dataString = JSON.stringify(data);
  } catch {
    dataString = String(data?.message ?? data?.href ?? 'unserializable');
  }
  // Dynamic-import (stale-chunk) failures are handled FIRST, ahead of every noise-
  // suppression rule, so that the "never silently swallow a stale-chunk error" invariant
  // holds by construction rather than by each suppression predicate remembering to guard
  // itself. Historically `isUseless()` ran first, which meant any signature that merely
  // over-matched a chunk-failure message (e.g. a bare 'Failed to fetch' vs. Vite's
  // 'Failed to fetch dynamically imported module') would disable the reload recovery AND
  // destroy its telemetry, with no compile error and no obvious test failure. Ordering the
  // check here makes that class of mistake impossible: suppression never sees these errors.
  const isStaleChunk = isDynamicImportError(combinedText(data));
  if (isStaleChunk) {
    if (isReload(data, dataString)) {
      // Navigating away to pull the current bundle — nothing to report.
      return;
    }
    // Reload guard armed: fall through to log it (downgraded to warn below).
  } else if (isUseless(data, dataString)) {
    return;
  }
  // Dedup/third-party-source exclusion still applies to both paths.
  if (isExcluded(data, dataString)) {
    return;
  }
  // A dynamic-import error that survived the reload attempt means reloadOnce() declined — the
  // one-shot reload guard is armed, so a deploy's stale chunk is persistently failing.
  // That is an expected post-deploy condition, not an app bug, so record it at warn
  // level regardless of which capture path produced it: the direct lazyWithReload log,
  // React's error passthrough of the rethrow, window.onerror, or vite:preloadError all
  // funnel here, so downgrading centrally (not just at the lazyWithReload call sites)
  // keeps the whole stale-chunk class out of the error alerting channel.
  const effectiveLevel: LogLevel = level === 'error' && isDynamicImportError(combinedText(data)) ? 'warn' : level;
  // Rate limiter: sliding window over WINDOW_MS; silently drop when at capacity.
  const now = Date.now();
  let i = 0;
  while (i < recentSendTimestamps.length && recentSendTimestamps[i] <= now - WINDOW_MS) {
    i++;
  }
  recentSendTimestamps.splice(0, i);
  if (recentSendTimestamps.length >= MAX_POSTS_PER_WINDOW) {
    return;
  }
  recentSendTimestamps.push(now);
  // Cap dedup array to the most recent 200 entries to bound memory.
  sent.push(dataString);
  if (sent.length > 200) {
    sent.shift();
  }
  void Axios.request({
    method: 'POST',
    url: `${authApiHost}/api/v1/public/client-log`,
    data: buildPayload(effectiveLevel, data),
  }).catch(() => {});
}

// Reusable structured logger for call sites that want to attach ad-hoc sanitized
// context (e.g. an HTTP status code, a request context name) without threading a
// full ISubmitData shape through. `extra` values are sanitized/truncated in
// buildPayload before being sent.
export function logErrorWithContext(message: string, extra: Record<string, string | number | boolean | undefined>) {
  submitData('error', { href: window.location.href, message, extra });
}

// hook to console methods
function handleIntercepted(level: LogLevel, args: IArguments) {
  const argArr = Array.from(args);
  const errArg = argArr.find((a): a is Error => a instanceof Error);
  // Lift name/message/stack off an Error arg. `message` is set (not just name/stack)
  // so the existing name+message suppression rules (Network Error, session-expiry,
  // isServerHttpError) still fire — omitting it would let an Error whose shape no
  // longer matches the exact-arguments rule leak through.
  const data: ISubmitData = {
    href: window.location.href,
    arguments: argArr.map((i) => i?.toString()),
    ...(errArg ? { name: errArg.name, message: errArg.message, stack: errArg.stack } : {}),
  };
  submitData(level, data);
}

function takeOverConsole() {
  const { console } = window;
  if (!console) {
    return;
  }

  function intercept(level: LogLevel, method: keyof Console) {
    const original = console[method] as any;
    (console as any)[method] = function () {
      try {
        handleIntercepted(level, arguments);
      } catch (err) {
        // ignore
      }
      return original.apply
        ? // do this for normal browsers
          original.apply(console, arguments)
        : // do this for IE
          original(Array.prototype.slice.apply(arguments).join(' '));
    } as any;
  }

  // Only intercept console.error — console.warn is NOT intercepted here to avoid
  // forwarding arbitrary third-party warn noise. Explicit logWarn calls submit directly.
  intercept('error', 'error');
}
takeOverConsole();

// hook to window.onerror
const previousWindowOnError = window.onerror;
window.onerror = function (...args) {
  const [message, source, lineno, colno, error] = args;
  const data: ISubmitData = {
    href: window.location.href,
    name: error?.name,
    message,
    source,
    stack: error?.stack,
    lineno,
    colno,
  };
  submitData('error', data);
  previousWindowOnError?.(...args);
};

// hook to window.unhandledrejection
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  // A rejection with no value at all (`Promise.reject()`, `throw undefined`) would otherwise
  // ship `String(reason)` — a log line whose entire message is the literal text "undefined",
  // with no name and no stack. Nothing in that is actionable, so drop it here at the source.
  // Detecting it HERE (where the real `reason` is in hand) rather than by pattern-matching the
  // flattened message later is what makes it precise: a genuine rejection that merely happens
  // to carry `message: 'undefined'` on a real object is still reported. The root cause belongs
  // at the rejecting call site — see the survey-tool-component PPButton fix.
  if (reason === null || reason === undefined) {
    return;
  }
  const data: ISubmitData = {
    href: window.location.href,
    name: reason?.name,
    message: reason?.message ?? String(reason),
    stack: reason?.stack,
    isSessionExpiry: reason?.isSessionExpiry,
  };
  submitData('error', data);
});

// Vite fires this on the window when a module preload fails (before the import
// rejection propagates). Recover by reloading once; preventDefault stops Vite's
// default behaviour of rethrowing the error. See
// https://vite.dev/guide/build.html#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  // Only swallow the event if we actually reloaded. If reloadOnce() declined (a
  // failure that persists past the one-shot guard), let the error propagate so it
  // still reaches telemetry instead of being silenced by preventDefault().
  if (reloadOnce()) {
    event.preventDefault();
  }
});

// export logging utilities
// logError routes through console.error, which is intercepted by takeOverConsole() — no double-submit.
// logWarn calls submitData directly (console.warn is NOT intercepted) and also calls console.warn.
export const log = {
  info: (msg: string) => console.log(msg),
  warn: (msg: string) => {
    submitData('warn', { href: window.location.href, message: msg });
    console.warn(msg);
  },
  error: (msg: string) => console.error(msg),
  debug: (...args: any[]) => {
    if (appEnv === 'development') {
      console.debug(...args);
    }
  },
  trace: (msg: string) => console.trace(msg),
};
export const logInfo = log.info;
export const logWarn = log.warn;
export const logError = log.error;
export const logTrace = log.trace;
